// Encryption + audit-chain status (Normal Core MASTER-PRD §6.5.6, #918) for
// Settings → Workspace → Data. Enforcement without inspection is invisible:
// this card shows whether the database is encrypted (and where the key comes
// from), the latest signed audit checkpoint over the event hash-chain, and a
// one-click "Verify chain" that runs verifyAudit end-to-end (chain intact,
// checkpoint heads match, signatures verify against the keychain key).

import { useState } from "react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  IntentBadge,
  SettingsSection,
  formatRelativeTime,
} from "../../index";
import { useApi, apiCall, ApiError } from "../../api/client";
import {
  security,
  type SecurityVerifyPayload,
} from "../../api/contracts/security";

const KEY_SOURCE_LABEL: Record<string, string> = {
  env: "environment key",
  keychain: "OS keychain",
  file: "local key file",
  none: "no app-level key — OS full-disk encryption is the at-rest baseline",
};

// Canonical user-facing copy for the key/encryption degradations (KEY-MANAGEMENT
// -SPEC §7.1). The status API supplies the ids + severity; the copy lives here.
const DEGRADATION_MSG: Record<string, string> = {
  "no-cipher": "Your database is not encrypted at rest — the SQLCipher driver isn't in this build. Reinstall to get it.",
  "encryption-off": "Encryption is disabled by EXCELLENT_DB_ENCRYPTION=off; the vault is plaintext. Unset the flag to re-encrypt.",
  "rekey-pending": "Encryption is pending — the vault was busy on every attempt. Quit other Excellent processes and it encrypts automatically.",
  "key-on-disk": "Encrypted, but the key is stored beside your data (the OS keychain wasn't available). A folder copy defeats it.",
  "cred-key-on-disk": "Integration credentials are effectively unprotected — the credential key is a plain file next to your database.",
  "no-recovery": "No recovery code — a lost keychain would be unrecoverable. Run `excellent-mcp keys recovery-setup` and store it off this machine.",
};

export function ChainStatusCard() {
  const status = useApi(security.status, {});
  const [verdict, setVerdict] = useState<SecurityVerifyPayload | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verify = async () => {
    setVerifying(true);
    setError(null);
    setVerdict(null);
    try {
      setVerdict(await apiCall(security.verifyChain, {}));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setVerifying(false);
    }
  };

  const d = status.data;

  return (
    <SettingsSection
      title="Security"
      description="Encryption at rest and the tamper-evident audit chain."
      id="security"
      actions={
        <Button variant="outline" onClick={verify} disabled={verifying || !d}>
          {verifying ? "Verifying…" : "Verify chain"}
        </Button>
      }
    >
      {!d ? (
        <div className="type-body text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-0">
          {d.encryption.degradations.length > 0 ? (
            <div className="space-y-2 pb-3">
              {d.encryption.degradations.map((deg) => (
                <Alert key={deg.id} variant={deg.severity === "high" ? "destructive" : "default"}>
                  <AlertDescription>{DEGRADATION_MSG[deg.id] ?? deg.id}</AlertDescription>
                </Alert>
              ))}
            </div>
          ) : null}
          <div className="flex items-center justify-between gap-4 border-b border-border/40 py-3 first:pt-0">
            <div>
              <div className="type-body font-medium">Encryption</div>
              <div className="type-subtext text-muted-foreground">
                {KEY_SOURCE_LABEL[d.encryption.keySource]}
              </div>
            </div>
            {d.encryption.encrypted ? (
              <IntentBadge intent="success" withDot>
                SQLCipher {d.encryption.cipherVersion}
              </IntentBadge>
            ) : (
              <IntentBadge intent="neutral" withDot>
                FDE fallback
              </IntentBadge>
            )}
          </div>

          <div className="flex items-center justify-between gap-4 border-b border-border/40 py-3 last:border-b-0 last:pb-0">
            <div>
              <div className="type-body font-medium">Audit chain</div>
              <div className="type-subtext text-muted-foreground">
                {d.chain.totalEvents} event{d.chain.totalEvents === 1 ? "" : "s"} in the hash chain
                {d.chain.checkpointId != null && d.chain.createdAt ? (
                  <>
                    {" · "}latest checkpoint #{d.chain.checkpointId},{" "}
                    {formatRelativeTime(d.chain.createdAt)}
                  </>
                ) : (
                  " · no checkpoints yet"
                )}
              </div>
              {d.chain.headHash ? (
                <code className="type-subtext text-muted-foreground">
                  head {d.chain.headHash.slice(0, 16)}…
                </code>
              ) : null}
            </div>
            {d.chain.checkpointId == null ? (
              <Badge variant="outline">unanchored</Badge>
            ) : d.chain.signed ? (
              <IntentBadge intent="success" withDot>
                signed
              </IntentBadge>
            ) : (
              <IntentBadge intent="warning" withDot>
                unsigned
              </IntentBadge>
            )}
          </div>

          {verdict ? (
            verdict.ok ? (
              <Alert className="mt-3">
                <AlertDescription>
                  Chain verified — every event hash links, every checkpoint head
                  matches, and all signed checkpoints verify ({verdict.checkpoints}{" "}
                  checkpoint{verdict.checkpoints === 1 ? "" : "s"} checked).
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive" className="mt-3">
                <AlertDescription>
                  Verification FAILED: {verdict.reason}
                  {verdict.checkpointId != null ? ` (checkpoint #${verdict.checkpointId})` : ""}
                  {verdict.brokenAt ? ` (broken at ${verdict.brokenAt})` : ""} — the
                  event log or checkpoints have been altered.
                </AlertDescription>
              </Alert>
            )
          ) : null}
          {error ? (
            <Alert variant="destructive" className="mt-3">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      )}
    </SettingsSection>
  );
}
